import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import BrandSelect from '../ui/BrandSelect';
import { mappingRowSelect } from '../utils/ReactSelectStyles';
import { Actions as DASHBOARD_ACTIONS } from '../../store/actions/consultantDashboardActions';
import { Actions as ORG_ACTIONS } from '../../store/actions/organisationActions';
import { toggleAlertMessage } from '../../store/action';
import { firmUserAssigneeOptions, formatFirmUserLabel, formatOwnerLabel } from './ownerDisplay';
import WorkItemActionStrip from './WorkItemActionStrip';
import WorkItemQueueTable from './WorkItemQueueTable';
import WorkItemDetailPanel from './WorkItemDetailPanel';
import {
  QUEUE_TABS,
  SORT_OPTIONS,
  PRIORITY_OPTIONS,
  inQueueTab,
  matchesFocusFilter,
  computeFocusCounts,
  computeQueueCounts,
  sortWorkItems,
} from './workItemQueueFilters';
import { parseTaskSearch, buildTaskSearch, canSeeWholeTeam } from './workItemUrlState';
import { resolveCurrentUserIds } from './currentUserIds';

const filterSelectStyles = mappingRowSelect;

/**
 * Mission-control task cockpit: action strip, dense queue, floating detail panel.
 * (R-7701–R-7705, R-7708, R-7710; presentation filters for overdue/stalled.)
 */
const WorkItemsMissionControl = ({
  dashboard,
  listWorkItems,
  getWorkItem,
  createWorkItem,
  updateWorkItem,
  postWorkItemMessage,
  requestAttachmentUpload,
  addWorkItemFiles,
  downloadWorkItemAttachment,
  removeWorkItemAttachment,
  renameWorkItemAttachment,
  createChaseDraft,
  approveChaseDraft,
  listProjects,
  createProject,
  addTimeEntry,
  updateTimeEntry,
  showAlert,
  profileDetails,
  firmOrgId,
  organisationUsers,
  getOrganisationUsers,
}) => {
  const { primary: profileSub, all: myUserIds } = useMemo(
    () =>
      resolveCurrentUserIds(profileDetails, [
        ...(organisationUsers || []),
        ...(dashboard?.teamFilter?.byMember || []),
      ]),
    [profileDetails, organisationUsers, dashboard?.teamFilter?.byMember],
  );
  const history = useHistory();
  const location = useLocation();
  const { id: routeId } = useParams();
  const initialUrl = useMemo(() => parseTaskSearch(location.search), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [tab, setTab] = useState(initialUrl.tab);
  const [focusFilter, setFocusFilter] = useState(initialUrl.focus);
  const [sortBy, setSortBy] = useState(initialUrl.sort || 'due');
  const [filterClient, setFilterClient] = useState(initialUrl.client);
  const [filterAssignee, setFilterAssignee] = useState(initialUrl.assignee);
  const [filterPriority, setFilterPriority] = useState(initialUrl.priority);
  const [filterProject, setFilterProject] = useState(initialUrl.project);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(routeId || null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectCatalog, setProjectCatalog] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newClientOrgId, setNewClientOrgId] = useState('');
  const [projectName, setProjectName] = useState('');
  const [panelTab, setPanelTab] = useState(initialUrl.panel || 'details');
  const skipUrlWrite = useRef(false);

  const teamView = canSeeWholeTeam(dashboard);
  const effectiveTab = tab || (teamView ? 'us' : 'me');

  const clients = dashboard?.clients || [];
  const clientOptions = useMemo(
    () =>
      clients.map((c) => ({
        value: c.clientOrgId,
        label: c.name || c.clientOrgId,
      })),
    [clients],
  );

  const myIdSet = useMemo(() => new Set((myUserIds || []).map(String)), [myUserIds]);

  // Firm Users (Cognito sub) — full colleague list, not only client owners.
  useEffect(() => {
    if (!firmOrgId || !getOrganisationUsers) return;
    getOrganisationUsers();
  }, [firmOrgId, getOrganisationUsers]);

  const assigneeOptions = useMemo(() => {
    const fromFirm = firmUserAssigneeOptions(organisationUsers);
    const opts = [];
    const seen = new Set();

    // Unassigned first
    const empty = fromFirm.find((o) => !o.value) || { value: '', label: 'Unassigned' };
    opts.push(empty);

    if (profileSub) {
      opts.push({ value: profileSub, label: 'Me' });
      seen.add(String(profileSub));
    }

    fromFirm.forEach((o) => {
      if (!o.value || seen.has(String(o.value))) return;
      seen.add(String(o.value));
      opts.push({
        value: o.value,
        label: myIdSet.has(String(o.value)) ? 'Me' : o.label,
      });
    });

    // Keep orphan assignees (left firm, or users list not loaded yet).
    const orphanIds = [
      ...items.map((i) => i.assigneeUserId),
      detail?.assigneeUserId,
      ...(dashboard?.teamFilter?.byMember || []).map((m) => m.ownerUserId),
    ].filter(Boolean);
    orphanIds.forEach((id) => {
      if (seen.has(String(id))) return;
      seen.add(String(id));
      const team = (dashboard?.teamFilter?.byMember || []).find(
        (m) => String(m.ownerUserId) === String(id),
      );
      opts.push({
        value: id,
        label: myIdSet.has(String(id))
          ? 'Me'
          : team
            ? formatOwnerLabel(team)
            : 'Team member',
      });
    });

    return opts;
  }, [
    organisationUsers,
    profileSub,
    detail?.assigneeUserId,
    items,
    myIdSet,
    dashboard?.teamFilter?.byMember,
  ]);

  const assigneeLabel = useCallback(
    (id) => assigneeOptions.find((o) => o.value === id)?.label || id || 'Unassigned',
    [assigneeOptions],
  );

  const resolveName = useCallback(
    (id) => {
      if (!id) return 'someone';
      if (myIdSet.has(String(id))) return 'You';
      const fromOpts = assigneeOptions.find((o) => o.value && String(o.value) === String(id));
      if (fromOpts?.label && fromOpts.label !== id && fromOpts.label !== 'Me') return fromOpts.label;
      const user = (organisationUsers || []).find((u) => String(u.sub) === String(id));
      if (user) return formatFirmUserLabel(user);
      const member = (dashboard?.teamFilter?.byMember || []).find(
        (m) => String(m.ownerUserId) === String(id),
      );
      if (member) return formatOwnerLabel(member);
      return 'Team member';
    },
    [assigneeOptions, organisationUsers, dashboard?.teamFilter?.byMember, myIdSet],
  );

  const resolveProjectName = useCallback(
    (id) => {
      if (!id) return null;
      const fromDetail = (projects || []).find((p) => String(p.id) === String(id));
      if (fromDetail?.name) return fromDetail.name;
      const fromCatalog = (projectCatalog || []).find((p) => String(p.id) === String(id));
      return fromCatalog?.name || null;
    },
    [projects, projectCatalog],
  );

  /** Update one item in the list without a full reload / spinner flicker. */
  const mergeItem = useCallback((item) => {
    if (!item?.id) return;
    setItems((prev) => {
      const idx = prev.findIndex((i) => String(i.id) === String(item.id));
      if (idx < 0) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], ...item };
      return next;
    });
  }, []);

  const loadProjectCatalog = useCallback(
    async (workItems) => {
      const clientIds = [
        ...new Set((workItems || []).map((i) => i.clientOrgId).filter(Boolean).map(String)),
      ];
      if (!clientIds.length) {
        setProjectCatalog([]);
        return;
      }
      const settled = await Promise.all(clientIds.map((id) => listProjects(id)));
      const byId = new Map();
      settled.forEach((res) => {
        (res?.projects || []).forEach((p) => {
          if (p?.id) byId.set(String(p.id), p);
        });
      });
      setProjectCatalog([...byId.values()]);
    },
    [listProjects],
  );

  const refreshList = useCallback(async () => {
    if (!firmOrgId) return;
    setLoading(true);
    // R-7702 queues: published only (drafts excluded from mission control)
    const result = await listWorkItems({ includeDrafts: false });
    if (result?.ok) {
      const nextItems = result.items || [];
      setItems(nextItems);
      loadProjectCatalog(nextItems);
    } else if (result?.error && result.error !== 'missing_organisation') {
      showAlert(true, result?.message || 'Could not load tasks.', 'error', 5000);
    }
    setLoading(false);
  }, [firmOrgId, listWorkItems, loadProjectCatalog, showAlert]);

  const loadDetail = useCallback(
    async (workItemId) => {
      if (!workItemId) {
        setDetail(null);
        return;
      }
      if (!firmOrgId) return;
      setDetailLoading(true);
      const result = await getWorkItem(workItemId);
      if (result?.ok) {
        setDetail(result.item);
        if (result.item?.clientOrgId) {
          const proj = await listProjects(result.item.clientOrgId);
          if (proj?.ok) setProjects(proj.projects || []);
        }
      } else if (result?.error !== 'missing_organisation') {
        showAlert(true, result?.message || 'Could not load work item.', 'error', 5000);
      }
      setDetailLoading(false);
    },
    [firmOrgId, getWorkItem, listProjects, showAlert],
  );

  useEffect(() => {
    if (!firmOrgId) return;
    refreshList();
  }, [firmOrgId, refreshList]);

  useEffect(() => {
    if (routeId) {
      setSelectedId(routeId);
      if (firmOrgId) loadDetail(routeId);
    } else {
      setSelectedId(null);
      setDetail(null);
    }
  }, [routeId, firmOrgId, loadDetail]);

  // Default queue tab once we know whether the firm has a multi-person team view.
  useEffect(() => {
    if (tab != null) return;
    if (!dashboard) return;
    setTab(teamView ? 'us' : 'me');
  }, [dashboard, teamView, tab]);

  // Keep filters shareable via query string (replace so filter tweaks don't stack history).
  useEffect(() => {
    if (tab == null) return;
    if (skipUrlWrite.current) {
      skipUrlWrite.current = false;
      return;
    }
    const nextSearch = buildTaskSearch({
      tab: effectiveTab,
      focus: focusFilter,
      sort: sortBy,
      client: filterClient,
      assignee: filterAssignee,
      priority: filterPriority,
      project: filterProject,
      panel: panelTab,
    });
    const path = selectedId ? `/consultant/tasks/${selectedId}` : '/consultant/tasks';
    const nextUrl = `${path}${nextSearch}`;
    if (`${location.pathname}${location.search}` !== nextUrl) {
      history.replace(nextUrl);
    }
  }, [
    effectiveTab,
    tab,
    focusFilter,
    sortBy,
    filterClient,
    filterAssignee,
    filterPriority,
    filterProject,
    panelTab,
    selectedId,
    history,
    location.pathname,
    location.search,
  ]);

  // Browser back/forward / pasted links.
  useEffect(() => {
    const parsed = parseTaskSearch(location.search);
    skipUrlWrite.current = true;
    if (parsed.tab && parsed.tab !== tab) setTab(parsed.tab);
    if (parsed.focus !== focusFilter) setFocusFilter(parsed.focus);
    if (parsed.sort !== sortBy) setSortBy(parsed.sort);
    if (parsed.client !== filterClient) setFilterClient(parsed.client);
    if (parsed.assignee !== filterAssignee) setFilterAssignee(parsed.assignee);
    if (parsed.priority !== filterPriority) setFilterPriority(parsed.priority);
    if (parsed.project !== filterProject) setFilterProject(parsed.project);
    if (parsed.panel !== panelTab) setPanelTab(parsed.panel);
    // Only react to search string changes from navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const focusCounts = useMemo(() => computeFocusCounts(items, myUserIds), [items, myUserIds]);
  const queueCounts = useMemo(() => computeQueueCounts(items, myUserIds), [items, myUserIds]);

  const projectFilterOptions = useMemo(() => {
    const opts = [
      { value: '', label: 'All projects' },
      { value: '__none__', label: 'No project' },
    ];
    const seen = new Set();
    const source = filterClient
      ? projectCatalog.filter((p) => String(p.clientOrgId) === String(filterClient))
      : projectCatalog;
    source.forEach((p) => {
      if (!p?.id || seen.has(String(p.id))) return;
      seen.add(String(p.id));
      opts.push({ value: p.id, label: p.name || p.id });
    });
    // Include projects referenced on visible items even if catalog load missed them.
    items.forEach((item) => {
      if (!item?.projectId || seen.has(String(item.projectId))) return;
      if (filterClient && String(item.clientOrgId) !== String(filterClient)) return;
      seen.add(String(item.projectId));
      const named = projectCatalog.find((p) => String(p.id) === String(item.projectId));
      opts.push({ value: item.projectId, label: named?.name || item.projectId });
    });
    return opts;
  }, [projectCatalog, filterClient, items]);

  const filtered = useMemo(() => {
    let list = items.filter((i) => inQueueTab(i, effectiveTab, myUserIds));
    if (focusFilter) {
      list = list.filter((i) => matchesFocusFilter(i, focusFilter, myUserIds));
    }
    if (filterClient) {
      list = list.filter((i) => String(i.clientOrgId) === String(filterClient));
    }
    if (filterAssignee === '__none__') {
      list = list.filter((i) => !i.assigneeUserId);
    } else if (filterAssignee) {
      // "Me" / self may appear under any of myUserIds (Cognito sub aliases).
      const assigneeIds = myIdSet.has(String(filterAssignee))
        ? myUserIds
        : [String(filterAssignee)];
      list = list.filter((i) =>
        assigneeIds.some((id) => String(i.assigneeUserId || '') === String(id)),
      );
    }
    if (filterPriority) {
      list = list.filter((i) => i.priority === filterPriority);
    }
    if (filterProject === '__none__') {
      list = list.filter((i) => !i.projectId);
    } else if (filterProject) {
      list = list.filter((i) => String(i.projectId || '') === String(filterProject));
    }
    return sortWorkItems(list, sortBy);
  }, [
    items,
    effectiveTab,
    focusFilter,
    myUserIds,
    filterClient,
    filterAssignee,
    filterPriority,
    filterProject,
    sortBy,
  ]);

  const openDetail = (id) => {
    setSelectedId(id);
    setPanelTab('details');
    loadDetail(id);
  };

  const closeDetail = useCallback(() => {
    setSelectedId(null);
    setDetail(null);
  }, []);

  const clientName = (clientOrgId) =>
    clients.find((c) => String(c.clientOrgId) === String(clientOrgId))?.name || clientOrgId;

  const saveField = async (patch) => {
    if (!detail) return { ok: false };
    const result = await updateWorkItem(detail.id, patch);
    if (result?.ok) {
      setDetail(result.item);
      mergeItem(result.item);
      return result;
    }
    showAlert(true, result?.message || 'Update failed.', 'error', 5000);
    return result;
  };

  const quickPatch = async (id, patch) => {
    const result = await updateWorkItem(id, patch);
    if (result?.ok) {
      if (selectedId === id) setDetail(result.item);
      mergeItem(result.item);
    } else {
      showAlert(true, result?.message || 'Update failed.', 'error', 5000);
    }
  };

  const handleCreate = async () => {
    if (!newTitle.trim() || !newClientOrgId) {
      showAlert(true, 'Title and client are required.', 'error', 4000);
      return;
    }
    const result = await createWorkItem(newClientOrgId, {
      title: newTitle.trim(),
      priority: 'med',
    });
    if (result?.ok) {
      setCreateOpen(false);
      setNewTitle('');
      showAlert(true, 'Work item created.', 'success', 3000);
      await refreshList();
      if (result.item?.id) openDetail(result.item.id);
    } else {
      showAlert(true, result?.message || 'Create failed.', 'error', 5000);
    }
  };

  const panelOpen = !!selectedId;
  const secondaryAssigneeOptions = useMemo(
    () => [{ value: '__none__', label: 'Unassigned' }, ...assigneeOptions.filter((o) => o.value)],
    [assigneeOptions],
  );

  return (
    <div className={`flex flex-col gap-4 ${panelOpen ? 'pr-0 lg:pr-[min(100vw,34rem)]' : ''}`}>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='flex items-center gap-2'>
          <Button variant='ghost' size='sm' onClick={() => history.push('/consultant/dashboard')}>
            <ArrowLeft size={16} strokeWidth={1.5} />
            Dashboard
          </Button>
          <div>
            <h1 className='text-xl font-semibold text-foreground'>Tasks</h1>
            <p className='text-sm text-muted-foreground'>Mission control for open work</p>
          </div>
        </div>
        <Button size='sm' onClick={() => setCreateOpen((v) => !v)}>
          <Plus size={16} strokeWidth={1.5} />
          New work item
        </Button>
      </div>

      {createOpen && (
        <Card className='bg-card'>
          <CardContent className='p-4 flex flex-wrap gap-3 items-end'>
            <div className='flex-1 min-w-[200px]'>
              <label className='text-xs text-muted-foreground'>Title</label>
              <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder='Task title' />
            </div>
            <div className='w-64 min-w-0'>
              <label className='text-xs text-muted-foreground'>Client</label>
              <BrandSelect
                styles={filterSelectStyles}
                options={clientOptions}
                value={clientOptions.find((o) => o.value === newClientOrgId) || null}
                onChange={(opt) => setNewClientOrgId(opt?.value || '')}
                placeholder='Select client'
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              />
            </div>
            <Button onClick={handleCreate}>Create</Button>
          </CardContent>
        </Card>
      )}

      <WorkItemActionStrip counts={focusCounts} activeId={focusFilter} onSelect={setFocusFilter} />

      <div className='flex flex-wrap gap-2'>
        {QUEUE_TABS.map((t) => (
          <Button
            key={t.id}
            size='sm'
            variant={effectiveTab === t.id ? 'default' : 'outline'}
            onClick={() => setTab(t.id)}>
            {t.label}
            <span className='ml-1.5 tabular-nums opacity-80'>{queueCounts[t.id] || 0}</span>
          </Button>
        ))}
      </div>

      <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 items-end'>
        <div className='min-w-0'>
          <label className='text-xs text-muted-foreground'>Client</label>
          <BrandSelect
            styles={filterSelectStyles}
            isClearable
            options={clientOptions}
            value={clientOptions.find((o) => o.value === filterClient) || null}
            onChange={(opt) => {
              setFilterClient(opt?.value || '');
              setFilterProject('');
            }}
            placeholder='All clients'
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          />
        </div>
        <div className='min-w-0'>
          <label className='text-xs text-muted-foreground'>Project</label>
          <BrandSelect
            styles={filterSelectStyles}
            isClearable
            options={projectFilterOptions.filter((o) => o.value)}
            value={projectFilterOptions.find((o) => o.value === filterProject) || null}
            onChange={(opt) => setFilterProject(opt?.value || '')}
            placeholder='All projects'
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          />
        </div>
        <div className='min-w-0'>
          <label className='text-xs text-muted-foreground'>Assignee</label>
          <BrandSelect
            styles={filterSelectStyles}
            isClearable
            options={secondaryAssigneeOptions}
            value={secondaryAssigneeOptions.find((o) => o.value === filterAssignee) || null}
            onChange={(opt) => setFilterAssignee(opt?.value || '')}
            placeholder='All assignees'
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          />
        </div>
        <div className='min-w-0'>
          <label className='text-xs text-muted-foreground'>Priority</label>
          <BrandSelect
            styles={filterSelectStyles}
            isClearable
            options={PRIORITY_OPTIONS}
            value={PRIORITY_OPTIONS.find((o) => o.value === filterPriority) || null}
            onChange={(opt) => setFilterPriority(opt?.value || '')}
            placeholder='Any'
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          />
        </div>
        <div className='min-w-0'>
          <label className='text-xs text-muted-foreground'>Sort</label>
          <BrandSelect
            styles={filterSelectStyles}
            options={SORT_OPTIONS.map((s) => ({ value: s.id, label: s.label }))}
            value={{
              value: sortBy,
              label: SORT_OPTIONS.find((s) => s.id === sortBy)?.label || 'Due date',
            }}
            onChange={(opt) => opt && setSortBy(opt.value)}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          />
        </div>
      </div>

      <Card className='bg-card'>
        <CardHeader className='pb-2'>
          <CardTitle className='text-sm flex flex-wrap items-center gap-2'>
            Open work ({filtered.length})
            <span className='text-xs font-normal text-muted-foreground'>
              Sorted by {SORT_OPTIONS.find((s) => s.id === sortBy)?.label || 'due date'}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <WorkItemQueueTable
            rows={filtered}
            loading={loading}
            selectedId={selectedId}
            clientName={clientName}
            assigneeLabel={assigneeLabel}
            assigneeOptions={assigneeOptions}
            resolveProjectName={resolveProjectName}
            resolveName={resolveName}
            onOpen={openDetail}
            onQuickPatch={quickPatch}
          />
        </CardContent>
      </Card>

      <WorkItemDetailPanel
        open={panelOpen}
        detail={detail}
        detailLoading={detailLoading}
        assigneeOptions={assigneeOptions}
        projects={projects}
        projectName={projectName}
        setProjectName={setProjectName}
        onClose={closeDetail}
        onSaveField={saveField}
        onDetailChange={setDetail}
        onItemUpdated={(item) => {
          setDetail(item);
          mergeItem(item);
        }}
        listProjects={listProjects}
        createProject={createProject}
        setProjects={setProjects}
        postWorkItemMessage={postWorkItemMessage}
        requestAttachmentUpload={requestAttachmentUpload}
        addWorkItemFiles={addWorkItemFiles}
        downloadWorkItemAttachment={downloadWorkItemAttachment}
        removeWorkItemAttachment={removeWorkItemAttachment}
        renameWorkItemAttachment={renameWorkItemAttachment}
        createChaseDraft={createChaseDraft}
        approveChaseDraft={approveChaseDraft}
        addTimeEntry={addTimeEntry}
        updateTimeEntry={updateTimeEntry}
        showAlert={showAlert}
        panelTab={panelTab}
        setPanelTab={setPanelTab}
        resolveName={resolveName}
        resolveProjectName={resolveProjectName}
        profileSub={profileSub}
        onProjectCreated={(project) => {
          if (!project?.id) return;
          setProjectCatalog((prev) => {
            const exists = (prev || []).some((p) => String(p.id) === String(project.id));
            return exists ? prev : [...(prev || []), project];
          });
        }}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  const raw = state.currentOrganisation || state.profile?.details?.consultantAdmin || '';
  const firmOrgId = String(raw || '').trim();
  const firmReady =
    firmOrgId && firmOrgId !== 'false' && firmOrgId !== 'null' && firmOrgId !== 'undefined'
      ? firmOrgId
      : '';
  return {
    dashboard: state.consultantDashboard?.dashboard || null,
    profileDetails: state.profile?.details || null,
    firmOrgId: firmReady,
    organisationUsers: state.organisation?.users || [],
  };
};

const mapDispatchToProps = (dispatch) => ({
  listWorkItems: (params) => dispatch(DASHBOARD_ACTIONS.listWorkItems(params)),
  getWorkItem: (id) => dispatch(DASHBOARD_ACTIONS.getWorkItem(id)),
  createWorkItem: (orgId, body) => dispatch(DASHBOARD_ACTIONS.createWorkItem(orgId, body)),
  updateWorkItem: (id, patch) => dispatch(DASHBOARD_ACTIONS.updateWorkItem(id, patch)),
  postWorkItemMessage: (id, body, internal, attachments) =>
    dispatch(DASHBOARD_ACTIONS.postWorkItemMessage(id, body, internal, attachments)),
  requestAttachmentUpload: (id, meta) => dispatch(DASHBOARD_ACTIONS.requestAttachmentUpload(id, meta)),
  addWorkItemFiles: (id, files) => dispatch(DASHBOARD_ACTIONS.addWorkItemFiles(id, files)),
  downloadWorkItemAttachment: (id, attachmentId) =>
    dispatch(DASHBOARD_ACTIONS.downloadWorkItemAttachment(id, attachmentId)),
  removeWorkItemAttachment: (id, attachmentId) =>
    dispatch(DASHBOARD_ACTIONS.removeWorkItemAttachment(id, attachmentId)),
  renameWorkItemAttachment: (id, attachmentId, name) =>
    dispatch(DASHBOARD_ACTIONS.renameWorkItemAttachment(id, attachmentId, name)),
  createChaseDraft: (id, body) => dispatch(DASHBOARD_ACTIONS.createChaseDraft(id, body)),
  approveChaseDraft: (id, draftId) => dispatch(DASHBOARD_ACTIONS.approveChaseDraft(id, draftId)),
  listProjects: (clientOrgId) => dispatch(DASHBOARD_ACTIONS.listProjects(clientOrgId)),
  createProject: (body) => dispatch(DASHBOARD_ACTIONS.createProject(body)),
  addTimeEntry: (id, body) => dispatch(DASHBOARD_ACTIONS.addTimeEntry(id, body)),
  updateTimeEntry: (id, entryId, patch) =>
    dispatch(DASHBOARD_ACTIONS.updateTimeEntry(id, entryId, patch)),
  getOrganisationUsers: () => dispatch(ORG_ACTIONS.getOrganisationUsers()),
  showAlert: (...args) => dispatch(toggleAlertMessage(...args)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WorkItemsMissionControl);
